import { Button } from '@storeblocks/button';
import { type FC } from 'react';
import styled from 'styled-components';

import { getAuth } from '@/auth/Auth';
import { appConfig } from '@/constants/config';
import { useTexts } from '@/hooks/useTexts';

import { Divider } from './Divider';
import { Column, Row } from './Grid';
import { WithGap } from './WithGap';

/**
 * Display a "no access" page for external users who do not have access
 * to the Connect platform.
 */
export const NoAccessExternalUser: FC = () => {
  const texts = useTexts();

  return (
    <div>
      <Center>
        <Page data-test="no-access-external-user-page">
          <StyledRow>
            <Column $span={7}>
              <StyledWithGap gap="48">
                <WithGap gap="8">
                  <h1>{texts.error.noAccess.external.title}</h1>
                  <div className="font-body">
                    {texts.error.description.orgs.part1}{' '}
                    <a href={`mailto:${texts.error.siosEmail}`}>
                      {texts.error.siosEmail}
                    </a>{' '}
                    {texts.error.description.orgs.part2}
                  </div>
                </WithGap>

                <Divider margin="0" />

                <WithGap>
                  <WithGap gap="8">
                    <h1>{texts.error.privateCustomer.title}</h1>
                    <div className="font-body">
                      {texts.error.privateCustomer.description}
                    </div>
                  </WithGap>

                  <WithGap gap="8" direction="row">
                    <div>
                      <Button
                        variant="tertiary"
                        iconRight="arrow_right"
                        as="a"
                        href={texts.error.privateCustomer.storebrand.norway.url}
                      >
                        {texts.error.privateCustomer.storebrand.norway.title}
                      </Button>
                    </div>
                    <div>
                      <Button
                        variant="tertiary"
                        iconRight="arrow_right"
                        as="a"
                        href={texts.error.privateCustomer.storebrand.sweden.url}
                      >
                        {texts.error.privateCustomer.storebrand.sweden.title}
                      </Button>
                    </div>
                  </WithGap>
                </WithGap>

                <Divider margin="0" />

                <WithGap gap="8">
                  <div>
                    <Button
                      variant="tertiary"
                      iconLeft="arrow_left"
                      as="a"
                      href={texts.error.privateCustomer.back.url}
                    >
                      {texts.error.privateCustomer.back.title}
                    </Button>
                  </div>
                  <div>
                    <Button
                      onClick={() => getAuth().logout()}
                      iconLeft="arrow_left"
                      variant="tertiary"
                    >
                      {texts.error.buttons.logOut}
                    </Button>
                  </div>
                </WithGap>
              </StyledWithGap>
            </Column>

            <StyledColumn $span={5}>
              <img
                alt="Something went wrong illustration"
                src={`${window.config.storeblocksAssetsBaseUrl}/illustrations/something-went-wrong.svg`}
                height="500"
              />
            </StyledColumn>
          </StyledRow>
        </Page>
      </Center>
    </div>
  );
};

const Center = styled.div`
  max-width: ${appConfig.maxPageWidth};
  margin: 0 auto;
`;

const Page = styled.div`
  margin-top: 72px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 24px;
`;

const StyledRow = styled(Row)`
  margin-top: 8px;
`;

const StyledColumn = styled(Column)`
  display: flex;
  justify-content: center;
`;

const StyledWithGap = styled(WithGap)`
  align-items: start;
`;
